// The option on the Schedule of Work (#412, docs/SURFACE-NAMES.md): the
// Schedule is made for a version, and each option has its own choices, so it
// uses the version's recommended option, else its first, and names it on the
// cover (DD, 2026-10-20). Each surface's figures are that option's, as pricing
// works them out (surfaceInOption): its coats, rate and prep, and its extra paint
// systems, which a surface's line also lists. Surfaces it leaves out aren't shown. Pure.
import { paintName, parseScope, surfaceInOption, type MeasuredSurface, type OptionScope, type PaintRate, type SurfacesDoc, type SurfaceType } from '@higgins/m2q-pricing';

/** The option whose paint systems the Schedule shows. */
export interface ScheduleSystems {
  optionName: string;
  scope: OptionScope;
  /** The option's paints as pricing rates them (pricing-reference.ts), so litres are the priced litres; none = the surfaces' own spread. */
  paints?: readonly PaintRate[];
}

interface OptionRecord {
  id: string;
  fields: Record<string, unknown>;
}

/** The version's recommended option, else its first; null when there is none or its choices can't be read. */
export function scheduleSystems(version: OptionRecord | null, options: readonly OptionRecord[]): ScheduleSystems | null {
  const option = options.find((o) => o.id === version?.fields.hig_recommendedoptionid) ?? options[0];
  if (!option) return null;
  try {
    const name = option.fields.hig_name;
    return { optionName: typeof name === 'string' && name.trim() ? name.trim() : 'the first option', scope: parseScope(option.fields.hig_scope as string | null) };
  } catch {
    return null;
  }
}

/** The surfaces the option leaves out of an area (none without an option). */
export function leftOutIn(areaId: string, systems: ScheduleSystems | null): ReadonlySet<string> {
  return new Set(Object.entries(systems?.scope.areas?.[areaId]?.surfaces ?? {}).flatMap(([id, s]) => (s.excluded === true ? [id] : [])));
}

/**
 * A surface's hours and litres as the option prices it (surfaceInOption; measured without an option), and with extra
 * paint systems, every system as "2 coats Durebuild" (`text`, null when it has only its own).
 */
export function scheduleSurface(areaId: string, doc: SurfacesDoc, surface: MeasuredSurface, type: SurfaceType | undefined, systems: ScheduleSystems | null) {
  const { rows, hours, litres } = surfaceInOption(doc, surface, type, systems?.scope.areas?.[areaId]?.surfaces?.[surface.id], systems?.paints);
  const coats = (n: number) => `${n} coat${n === 1 ? '' : 's'}`;
  const text = systems && rows.length > 1 ? rows.map((r) => [coats(r.coats), paintName(systems.scope, r.paintId)].filter(Boolean).join(' ')).join('; ') : null;
  return { hours, litres, text };
}
